import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { Lang, Translations } from "../types";
import { COMPANY, LOCATIONS } from "../config";
import { STORAGE_KEYS } from "../_internal";
import ro from "../locales/ro.json";
import en from "../locales/en.json";

type RawTranslations = typeof ro;

function buildTranslations(raw: RawTranslations): Translations {
  return {
    ...raw,
    company: {
      ...raw.company,
      name: COMPANY.name,
      phone: COMPANY.phone,
      email: COMPANY.email,
      address: COMPANY.address,
      cif: COMPANY.cif,
      founded: String(COMPANY.founded),
    },
    locations: {
      ...raw.locations,
      items: LOCATIONS.map((loc) => ({
        name: loc.name,
        address: loc.address,
        phone: loc.phone,
        schedule: loc.schedule,
        type: loc.type,
      })),
    },
  } as Translations;
}

const TRANSLATIONS: Record<Lang, Translations> = {
  ro: buildTranslations(ro as RawTranslations),
  en: buildTranslations(en as RawTranslations),
};

function readLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.lang);
    return stored === "en" || stored === "ro" ? stored : "ro";
  } catch {
    return "ro";
  }
}

function writeLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEYS.lang, lang);
  } catch {}
}

interface LangContextValue {
  lang: Lang;
  switchLang: (l: Lang) => void;
  t: Translations;
}

const LangContext = createContext<LangContextValue | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(readLang);

  const switchLang = useCallback((l: Lang) => {
    writeLang(l);
    setLang(l);
  }, []);

  return (
    <LangContext.Provider value={{ lang, switchLang, t: TRANSLATIONS[lang] }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used inside LangProvider");
  return ctx;
}
